import { SessionProfile } from '../routes/manifest';

export const SESSION_UNAUTHORIZED_EVENT = 'session:unauthorized';

let sessionGeneration = 0;
let sessionAbort: AbortController | null = null;
let sessionPromise: Promise<SessionProfile | null> | null = null;

export class SessionFetchError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = 'SessionFetchError';
    if (options && 'cause' in options) {
      (this as Error & { cause?: unknown }).cause = options.cause;
    }
  }
}

export async function getSession(options?: { force?: boolean }): Promise<SessionProfile | null> {
  if (options?.force) clearSession();
  if (!sessionPromise) {
    const generation = sessionGeneration;
    const controller = new AbortController();
    sessionAbort = controller;
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    const pending = fetchSession(controller.signal).then(session => {
      if (generation !== sessionGeneration) throw new SessionFetchError('Session request superseded');
      return session;
    }).finally(() => {
      window.clearTimeout(timeout);
      if (sessionPromise === pending) {
        sessionPromise = null;
        sessionAbort = null;
      }
    });
    sessionPromise = pending;
  }
  return sessionPromise;
}

async function fetchSession(signal: AbortSignal): Promise<SessionProfile | null> {
  try {
    const response = await fetch('/api/v1/auth/session', {
      method: 'GET',
      signal,
      cache: 'no-store',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-ID': generateRequestId()
      }
    });

    if (response.status === 401) {
      // Not authenticated
      return null;
    }

    if (!response.ok) {
      throw new SessionFetchError(`Session fetch failed: ${response.status}`);
    }

    const sessionData = await response.json();
    if (!Number.isSafeInteger(sessionData.authorizationExpiresAt) || sessionData.authorizationExpiresAt <= Date.now()) {
      throw new SessionFetchError('Session permission view expired');
    }
    
    // Ensure roles and scopes are arrays to prevent null/undefined errors
    // Map 'id' field from API to 'userId' expected by frontend
    const session: SessionProfile = {
      ...sessionData,
      userId: sessionData.id || sessionData.userId,
      roles: sessionData.roles || [],
      scopes: sessionData.scopes || [],
      navTree: sessionData.navTree || []
    };
    return session;
  } catch (error) {
    console.error('Failed to fetch session:', error);
    if (error instanceof SessionFetchError) {
      throw error;
    }
    throw new SessionFetchError('Failed to fetch session', { cause: error });
  }
}

export function clearSession(): void {
  sessionGeneration += 1;
  sessionAbort?.abort();
  sessionAbort = null;
  sessionPromise = null;
}

export function notifySessionExpired(): void {
  clearSession();
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(SESSION_UNAUTHORIZED_EVENT));
  }
}

export async function login(returnUrl?: string, realm?: string): Promise<void> {
  const params = new URLSearchParams();
  if (returnUrl) {
    params.set('return_url', returnUrl);
  }
  if (realm) {
    params.set('realm', realm);
  }

  window.location.href = `/api/v1/auth/login?${params.toString()}`;
}

export async function logout(): Promise<void> {
  clearSession();
  const params = new URLSearchParams({ return_url: '/?signed_out=1' });
  window.location.assign(`/api/v1/auth/logout?${params.toString()}`);
}

function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}
